import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, COACHES, SESSION_TYPES, LOCATIONS } from "@/lib/api";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  ArrowLeft,
  ArrowRight,
  CalendarIcon,
  CheckCircle2,
  Clock,
  MapPin,
  Sparkles,
  Sliders,
  User2,
  Users2,
} from "lucide-react";

const TIME_SLOTS = [
  "16:00", "16:30", "17:00", "17:30", "18:00", "18:30", "19:00", "19:30",
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "12:00", "13:00", "14:00", "15:00",
];

const FOCUS_AREAS = [
  "First touch",
  "Finishing",
  "1v1 attacking",
  "1v1 defending",
  "Passing range",
  "Dribbling",
  "Crossing",
  "Heading",
  "Set pieces",
  "Tactical awareness",
  "Speed & agility",
  "Strength & conditioning",
  "Match fitness",
  "Recovery from injury",
  "Goalkeeping",
  "Decision making",
];

export default function Booking() {
  const [params] = useSearchParams();
  const initialCoach = params.get("coach") === "james" ? "james" : params.get("coach") === "jacob" ? "jacob" : "jacob";
  const navigate = useNavigate();

  const [form, setForm] = useState({
    player_name: "",
    player_age: "",
    player_position: "",
    preferred_foot: "",
    current_club: "",
    current_level: "",
    second_player_name: "",
    second_player_age: "",
    parent_name: "",
    parent_email: "",
    parent_phone: "",
    coach: initialCoach,
    session_type: "skill_specific",
    location: "southampton_sports_centre",
    package: "single",
    focus_areas: [],
    custom_request: "",
    equipment_needed: "",
    preferred_date: null, // Date object
    preferred_time: "17:00",
    home_address: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const coach = COACHES[form.coach];

  const priceQuote = useMemo(() => {
    const c = COACHES[form.coach];
    if (!c) return null;
    if (form.package === "single") return { unit: c.price, total: c.price, label: "1 session" };
    if (form.package === "block_3")
      return { unit: 16.67, total: 50, label: "3 sessions" };
    if (form.package === "block_5")
      return { unit: c.id === "james" ? 45 : 18, total: c.id === "james" ? 225 : 90, label: "5 sessions" };
    if (form.package === "block_10")
      return { unit: c.id === "james" ? 42.5 : 17, total: c.id === "james" ? 425 : 170, label: "10 sessions" };
    if (form.package === "pair")
      return { unit: 15, total: 30, label: "2-player session" };
    return null;
  }, [form.coach, form.package]);

  // Jacob-only packages: revert to single if james is picked
  useEffect(() => {
    if (form.coach === "james" && (form.package === "block_3" || form.package === "pair")) {
      setForm((f) => ({ ...f, package: "single" }));
    }
  }, [form.coach, form.package]);

  // James only offers u16_u18_elite; if user picks elite, force coach to james
  useEffect(() => {
    if (form.session_type === "u16_u18_elite" && form.coach !== "james") {
      setForm((f) => ({ ...f, coach: "james" }));
    }
  }, [form.session_type, form.coach]);

  const validate = () => {
    const required = [
      ["player_name", "Player name"],
      ["player_age", "Player age"],
      ["parent_name", "Parent / guardian name"],
      ["parent_email", "Email"],
      ["parent_phone", "Phone"],
      ["preferred_date", "Preferred date"],
      ["preferred_time", "Preferred time"],
    ];
    for (const [k, label] of required) {
      if (!form[k]) {
        toast.error(`${label} is required`);
        return false;
      }
    }
    if (Number(form.player_age) < 8 || Number(form.player_age) > 25) {
      toast.error("Player age must be between 8 and 25");
      return false;
    }
    if (form.location === "at_home" && !form.home_address.trim()) {
      toast.error("Please enter an address for at-home sessions");
      return false;
    }
    if (form.package === "pair") {
      if (!form.second_player_name.trim()) {
        toast.error("Second player's name is required for a 2-player session");
        return false;
      }
      if (!form.second_player_age) {
        toast.error("Second player's age is required for a 2-player session");
        return false;
      }
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        player_age: Number(form.player_age),
        second_player_age: form.second_player_age ? Number(form.second_player_age) : null,
        second_player_name: form.second_player_name || null,
        preferred_date: format(form.preferred_date, "yyyy-MM-dd"),
      };
      await api.post("/bookings", payload);
      toast.success("Booking request sent!");
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <section className="py-20 md:py-32">
          <div className="max-w-2xl mx-auto px-5 text-center">
            <div className="w-20 h-20 rounded-3xl bg-emerald-100 text-emerald-600 grid place-items-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-slate-950">
              Request received.
            </h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              Thanks {form.parent_name.split(" ")[0]}. We've logged the request for{" "}
              <span className="font-semibold text-slate-900">{form.player_name}</span> with{" "}
              <span className="font-semibold text-slate-900">{COACHES[form.coach].name}</span>{" "}
              on <span className="font-semibold text-slate-900">{format(form.preferred_date, "EEE d MMM yyyy")}</span>{" "}
              at <span className="font-semibold text-slate-900">{form.preferred_time}</span>.
              <br />
              We'll confirm by email within 24 hours.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/">
                <Button data-testid="success-home-btn" variant="outline" className="rounded-full px-7 py-5">
                  Back to home
                </Button>
              </Link>
              <Button
                data-testid="success-new-btn"
                onClick={() => { setSubmitted(false); navigate("/book"); }}
                className="rounded-full bg-blue-600 hover:bg-blue-700 text-white px-7 py-5"
              >
                Book another session
              </Button>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section data-testid="booking-page" className="pt-12 md:pt-16 pb-24 bg-slate-50 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <Link to="/" data-testid="booking-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="mb-8">
                <div className="text-xs uppercase tracking-[0.18em] font-bold text-blue-600 mb-3">
                  Request a Session
                </div>
                <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-slate-950">
                  Let&apos;s build the session.
                </h1>
                <p className="mt-3 text-slate-600 text-lg">
                  Only 6 things are actually needed — everything else is optional and just helps us tailor the session. We&apos;ll confirm within 24 hours.
                </p>
              </div>

              <form onSubmit={submit} className="space-y-6">
                {/* Coach selection */}
                <Card className="p-6 md:p-7 rounded-3xl border-slate-200">
                  <SectionTitle icon={Users2} label="Pick your coach" />
                  <RadioGroup
                    value={form.coach}
                    onValueChange={(v) => update("coach", v)}
                    className="mt-5 grid sm:grid-cols-2 gap-3"
                  >
                    {Object.values(COACHES).map((c) => {
                      const selected = form.coach === c.id;
                      return (
                        <label
                          key={c.id}
                          htmlFor={`coach-${c.id}`}
                          data-testid={`coach-radio-${c.id}`}
                          className={`cursor-pointer rounded-2xl border p-5 transition ${selected ? "border-blue-600 bg-blue-50/50 ring-1 ring-blue-200" : "border-slate-200 bg-white hover:border-slate-300"}`}
                        >
                          <div className="flex items-start gap-3">
                            <RadioGroupItem value={c.id} id={`coach-${c.id}`} className="mt-1" />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <div className="font-display font-extrabold text-slate-950">
                                  {c.name}
                                </div>
                                <div className="text-sm font-bold text-slate-900">£{c.price}/hr</div>
                              </div>
                              <div className="text-xs text-slate-500 mt-1">{c.tagline}</div>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </RadioGroup>
                </Card>

                {/* Session — merged focus + customise */}
                <Card className="p-6 md:p-7 rounded-3xl border-slate-200">
                  <SectionTitle icon={Sparkles} label="The session" />
                  <div className="grid md:grid-cols-2 gap-4 mt-5">
                    <Field label="Session type" htmlFor="session_type">
                      <Select
                        value={form.session_type}
                        onValueChange={(v) => update("session_type", v)}
                      >
                        <SelectTrigger id="session_type" data-testid="select-session-type" className="rounded-xl h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SESSION_TYPES.map((s) => (
                            <SelectItem key={s.id} value={s.id} data-testid={`session-type-${s.id}`}>
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 mt-2">
                        {SESSION_TYPES.find((s) => s.id === form.session_type)?.desc}
                      </p>
                    </Field>

                    <Field label="Package" htmlFor="package">
                      <Select value={form.package} onValueChange={(v) => update("package", v)}>
                        <SelectTrigger id="package" data-testid="select-package" className="rounded-xl h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single" data-testid="package-single">Single session</SelectItem>
                          {form.coach === "jacob" && (
                            <SelectItem value="block_3" data-testid="package-block-3">3 sessions for £50 (Jacob)</SelectItem>
                          )}
                          <SelectItem value="block_5" data-testid="package-block-5">Block of 5 (save)</SelectItem>
                          <SelectItem value="block_10" data-testid="package-block-10">Block of 10 (save more)</SelectItem>
                          {form.coach === "jacob" && (
                            <SelectItem value="pair" data-testid="package-pair">2-player session — £30 total (£15 each)</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      {priceQuote && (
                        <p className="text-xs text-slate-600 mt-2">
                          <span className="font-bold text-slate-900">£{priceQuote.total}</span> total · {priceQuote.label} · £{priceQuote.unit}/session
                        </p>
                      )}
                    </Field>
                  </div>

                  {/* Optional customisation — collapsible */}
                  <details className="mt-6 group" data-testid="customise-details">
                    <summary className="flex items-center gap-2 cursor-pointer list-none select-none py-2 text-sm font-semibold text-slate-700 hover:text-slate-950">
                      <Sliders className="w-4 h-4 text-blue-600" />
                      Customise the session
                      <span className="text-xs font-normal text-slate-400 ml-1">(optional — focus areas, kit, custom requests)</span>
                      <span className="ml-auto text-slate-400 group-open:rotate-180 transition-transform">▾</span>
                    </summary>

                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <Label className="text-sm font-semibold text-slate-800">Focus areas <span className="text-slate-400 font-normal">(pick any)</span></Label>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {FOCUS_AREAS.map((f) => {
                          const active = form.focus_areas.includes(f);
                          return (
                            <button
                              type="button"
                              key={f}
                              data-testid={`focus-chip-${f.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                              onClick={() =>
                                update(
                                  "focus_areas",
                                  active ? form.focus_areas.filter((x) => x !== f) : [...form.focus_areas, f]
                                )
                              }
                              className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition ${active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"}`}
                            >
                              {f}
                            </button>
                          );
                        })}
                      </div>

                      <div className="mt-5">
                        <Field label="Kit / equipment notes (optional)" htmlFor="equipment_needed">
                          <Input
                            id="equipment_needed"
                            data-testid="input-equipment"
                            className="rounded-xl h-11"
                            value={form.equipment_needed}
                            onChange={(e) => update("equipment_needed", e.target.value)}
                            placeholder="e.g. moulded boots, GK gloves, water"
                          />
                        </Field>
                      </div>

                      <div className="mt-5">
                        <Field label="Anything specific you want from the session? (optional)" htmlFor="custom_request">
                          <Textarea
                            id="custom_request"
                            data-testid="input-custom-request"
                            rows={4}
                            className="rounded-xl"
                            value={form.custom_request}
                            onChange={(e) => update("custom_request", e.target.value)}
                            placeholder="e.g. 'Focus on left-foot finishing', 'confidence on the ball under pressure'…"
                          />
                        </Field>
                      </div>
                    </div>
                  </details>
                </Card>

                {/* Location */}
                <Card className="p-6 md:p-7 rounded-3xl border-slate-200">
                  <SectionTitle icon={MapPin} label="Where should it happen?" />
                  <RadioGroup
                    value={form.location}
                    onValueChange={(v) => update("location", v)}
                    className="mt-5 grid gap-3"
                  >
                    {LOCATIONS.map((loc) => {
                      const selected = form.location === loc.id;
                      return (
                        <label
                          key={loc.id}
                          htmlFor={`loc-${loc.id}`}
                          data-testid={`location-radio-${loc.id}`}
                          className={`cursor-pointer rounded-2xl border p-4 flex items-start gap-3 ${selected ? "border-emerald-500 bg-emerald-50/40 ring-1 ring-emerald-200" : "border-slate-200 bg-white hover:border-slate-300"}`}
                        >
                          <RadioGroupItem value={loc.id} id={`loc-${loc.id}`} className="mt-1" />
                          <div>
                            <div className="font-bold text-slate-900">{loc.label}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{loc.note}</div>
                          </div>
                        </label>
                      );
                    })}
                  </RadioGroup>
                  {form.location === "at_home" && (
                    <div className="mt-4">
                      <Label htmlFor="home_address">Home address</Label>
                      <Input
                        id="home_address"
                        data-testid="input-home-address"
                        className="rounded-xl mt-2 h-11"
                        value={form.home_address}
                        onChange={(e) => update("home_address", e.target.value)}
                        placeholder="Street, Town, Postcode"
                      />
                    </div>
                  )}
                </Card>

                {/* Date & time */}
                <Card className="p-6 md:p-7 rounded-3xl border-slate-200">
                  <SectionTitle icon={CalendarIcon} label="Preferred date & time" />
                  <div className="grid md:grid-cols-2 gap-5 mt-5">
                    <Field label="Date" htmlFor="date">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            id="date"
                            data-testid="btn-pick-date"
                            type="button"
                            variant="outline"
                            className="w-full h-11 justify-start rounded-xl font-normal"
                          >
                            <CalendarIcon className="w-4 h-4 mr-2 text-slate-500" />
                            {form.preferred_date
                              ? format(form.preferred_date, "EEE d MMM yyyy")
                              : "Pick a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="p-0 w-auto">
                          <Calendar
                            mode="single"
                            selected={form.preferred_date}
                            onSelect={(d) => update("preferred_date", d)}
                            disabled={(date) => {
                              const today = new Date();
                              today.setHours(0,0,0,0);
                              return date < today;
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </Field>
                    <Field label="Time" htmlFor="time">
                      <Select value={form.preferred_time} onValueChange={(v) => update("preferred_time", v)}>
                        <SelectTrigger id="time" data-testid="select-time" className="rounded-xl h-11">
                          <Clock className="w-4 h-4 mr-2 text-slate-500" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_SLOTS.map((t) => (
                            <SelectItem key={t} value={t} data-testid={`time-${t}`}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                </Card>

                {/* Player */}
                <Card className="p-6 md:p-7 rounded-3xl border-slate-200">
                  <SectionTitle icon={User2} label="Player details" />
                  <div className="grid md:grid-cols-2 gap-4 mt-5">
                    <Field label="Player's name" htmlFor="player_name">
                      <Input id="player_name" data-testid="input-player-name" className="rounded-xl h-11" value={form.player_name} onChange={(e)=>update("player_name", e.target.value)} placeholder="e.g. Harry Smith" />
                    </Field>
                    <Field label="Age" htmlFor="player_age">
                      <Input id="player_age" data-testid="input-player-age" type="number" min={8} max={25} className="rounded-xl h-11" value={form.player_age} onChange={(e)=>update("player_age", e.target.value)} placeholder="e.g. 14" />
                    </Field>
                  </div>

                  <details className="mt-5 group" data-testid="player-more-details">
                    <summary className="flex items-center gap-2 cursor-pointer list-none select-none py-2 text-sm font-semibold text-slate-700 hover:text-slate-950">
                      <User2 className="w-4 h-4 text-blue-600" />
                      More about the player
                      <span className="text-xs font-normal text-slate-400 ml-1">(optional — helps us tailor the session)</span>
                      <span className="ml-auto text-slate-400 group-open:rotate-180 transition-transform">▾</span>
                    </summary>
                    <div className="mt-3 pt-3 border-t border-slate-100 grid md:grid-cols-2 gap-4">
                      <Field label="Position" htmlFor="player_position">
                        <Input id="player_position" data-testid="input-player-position" className="rounded-xl h-11" value={form.player_position} onChange={(e)=>update("player_position", e.target.value)} placeholder="e.g. Right winger" />
                      </Field>
                      <Field label="Preferred foot" htmlFor="preferred_foot">
                        <Select value={form.preferred_foot} onValueChange={(v) => update("preferred_foot", v)}>
                          <SelectTrigger id="preferred_foot" data-testid="select-preferred-foot" className="rounded-xl h-11">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="right" data-testid="foot-right">Right</SelectItem>
                            <SelectItem value="left" data-testid="foot-left">Left</SelectItem>
                            <SelectItem value="both" data-testid="foot-both">Both / two-footed</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Current club" htmlFor="current_club">
                        <Input id="current_club" data-testid="input-current-club" className="rounded-xl h-11" value={form.current_club} onChange={(e)=>update("current_club", e.target.value)} placeholder="e.g. Hythe Aztecs U14" />
                      </Field>
                      <Field label="Current level" htmlFor="current_level">
                        <Input id="current_level" data-testid="input-current-level" className="rounded-xl h-11" value={form.current_level} onChange={(e)=>update("current_level", e.target.value)} placeholder="e.g. Grassroots, JPL, Academy trialist" />
                      </Field>
                    </div>
                  </details>

                  {form.package === "pair" && (
                    <div className="mt-6 pt-6 border-t border-dashed border-slate-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Users2 className="w-4 h-4 text-emerald-600" />
                        <div className="text-sm font-bold text-slate-900">Second player (2-player session)</div>
                      </div>
                      <p className="text-xs text-slate-500 mb-4">The £30 fee is split between both players (£15 each).</p>
                      <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Second player's name" htmlFor="second_player_name">
                          <Input id="second_player_name" data-testid="input-second-player-name" className="rounded-xl h-11" value={form.second_player_name} onChange={(e)=>update("second_player_name", e.target.value)} placeholder="e.g. Alex Jones" />
                        </Field>
                        <Field label="Second player's age" htmlFor="second_player_age">
                          <Input id="second_player_age" data-testid="input-second-player-age" type="number" min={8} max={25} className="rounded-xl h-11" value={form.second_player_age} onChange={(e)=>update("second_player_age", e.target.value)} placeholder="e.g. 13" />
                        </Field>
                      </div>
                    </div>
                  )}
                </Card>

                {/* Parent contact */}
                <Card className="p-6 md:p-7 rounded-3xl border-slate-200">
                  <SectionTitle icon={Users2} label="Parent / guardian contact" />
                  <div className="grid md:grid-cols-2 gap-4 mt-5">
                    <Field label="Your name" htmlFor="parent_name">
                      <Input id="parent_name" data-testid="input-parent-name" className="rounded-xl h-11" value={form.parent_name} onChange={(e)=>update("parent_name", e.target.value)} placeholder="e.g. Sarah Smith" />
                    </Field>
                    <Field label="Phone" htmlFor="parent_phone">
                      <Input id="parent_phone" data-testid="input-parent-phone" className="rounded-xl h-11" value={form.parent_phone} onChange={(e)=>update("parent_phone", e.target.value)} placeholder="07..." />
                    </Field>
                    <Field label="Email" htmlFor="parent_email" className="md:col-span-2">
                      <Input id="parent_email" data-testid="input-parent-email" type="email" className="rounded-xl h-11" value={form.parent_email} onChange={(e)=>update("parent_email", e.target.value)} placeholder="you@email.com" />
                    </Field>
                    <Field label="Notes (optional)" htmlFor="notes" className="md:col-span-2">
                      <Textarea id="notes" data-testid="input-notes" rows={3} className="rounded-xl" value={form.notes} onChange={(e)=>update("notes", e.target.value)} placeholder="Injuries, medical info, preferred contact times, anything we should know…" />
                    </Field>
                  </div>
                </Card>

                <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between pt-2">
                  <p className="text-xs text-slate-500">
                    By submitting you agree to be contacted by Grant Football Coaching to confirm your session.
                  </p>
                  <Button
                    type="submit"
                    data-testid="submit-booking-btn"
                    disabled={submitting}
                    className="rounded-full bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 font-bold shadow-soft"
                  >
                    {submitting ? "Sending…" : "Send booking request"}
                    {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
                  </Button>
                </div>
              </form>
            </div>

            {/* Sidebar summary */}
            <aside className="lg:col-span-1">
              <div className="sticky top-24 space-y-4">
                <Card data-testid="booking-summary" className="rounded-3xl border-slate-200 p-6 bg-white">
                  <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">
                    Your request
                  </div>
                  <div className="space-y-3 text-sm">
                    <SummaryRow label="Coach" value={coach.name} />
                    <SummaryRow label="Type" value={SESSION_TYPES.find(s=>s.id===form.session_type)?.label} />
                    <SummaryRow label="Package" value={
                      form.package === "single" ? "Single session"
                      : form.package === "block_3" ? "3 sessions (£50)"
                      : form.package === "block_5" ? "Block of 5"
                      : form.package === "block_10" ? "Block of 10"
                      : form.package === "pair" ? "2-player session"
                      : form.package
                    } />
                    <SummaryRow label="Location" value={LOCATIONS.find(l=>l.id===form.location)?.label} />
                    <SummaryRow label="Date" value={form.preferred_date ? format(form.preferred_date, "d MMM yyyy") : "Not set"} />
                    <SummaryRow label="Time" value={form.preferred_time} />
                  </div>
                  {form.focus_areas.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Focus</div>
                      <div className="flex flex-wrap gap-1.5">
                        {form.focus_areas.map((f) => (
                          <span key={f} className="text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-full">
                            {f}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {priceQuote && (
                    <div className="mt-5 pt-5 border-t border-slate-200">
                      <div className="flex items-baseline justify-between">
                        <div className="text-sm text-slate-500 font-semibold">Estimated total</div>
                        <div className="font-display text-3xl font-black text-slate-950">£{priceQuote.total}</div>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{priceQuote.label} · £{priceQuote.unit}/session</div>
                    </div>
                  )}
                </Card>
                <Card className="rounded-3xl border-slate-200 p-6 bg-slate-950 text-white">
                  <div className="font-display text-lg font-extrabold">What happens next</div>
                  <ol className="mt-3 space-y-2 text-sm text-white/80 list-decimal pl-4">
                    <li>We review your request within 24 hours.</li>
                    <li>We confirm the slot by email or phone.</li>
                    <li>Payment details shared on confirmation.</li>
                  </ol>
                </Card>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function SectionTitle({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-700 grid place-items-center border border-blue-100">
        <Icon className="w-4 h-4" />
      </div>
      <h3 className="font-display font-extrabold text-lg text-slate-950 tracking-tight">{label}</h3>
    </div>
  );
}

function Field({ label, htmlFor, children, className = "" }) {
  return (
    <div className={className}>
      <Label htmlFor={htmlFor} className="text-sm font-semibold text-slate-800">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="text-slate-500">{label}</div>
      <div className="font-semibold text-slate-900 text-right">{value || "—"}</div>
    </div>
  );
}
